import React, { useState, useEffect } from "react";
import { CircularProgress } from "@material-ui/core";

// screen components
import SelectPaymentMethod from "./states/SelectPaymentMethod";
import SelectShareLinkOptions from "./states/SelectShareLinkOptions";
import SuccessWithGeneratedLink from "./states/SuccessWithGeneratedLink";
import SuccessTotalPaymentWithWallet from "./states/SuccessTotalPaymentWithWallet";
import ErrorInCreatingAppointment from "./states/ErrorInCreatingAppointment";
import AptCreatedButLinkNotGenerated from "./states/AptCreatedButLinkNotGenerated";

import { SCREEN_STATE, PAYMENT_METHOD } from "./paymentLinkConstants";
import "../../../style/PaymentLinkDialog.css";
import { formatCurrency } from "../../../helper/formatCurrency";
import NavigoDashboardUtils from "../../../services/NavigoDashboardUtils";
import { showNotification } from "@mantine/notifications";
import { APPOINTMENT_PAYMENT_STATUS } from "../../../helper/appointmentConstants";
import Dialog from "@material-ui/core/Dialog";
import { IconX } from "@tabler/icons";

interface PaymentLinkDialogProps {
  visible?: boolean;
  onClose?: () => void;
  appointment?: any;
  withCreateAppointment?: boolean;
  payloadForCreateAppointment?: any;
  aptPrice?: number | null;
  props?: any;
  profileDetails?: any;
  refreshAptDetailScreenData?: () => void;
}

const PaymentLinkDialog: React.FC<PaymentLinkDialogProps> = ({
  visible = false,
  onClose = () => {},
  appointment,
  withCreateAppointment = false,
  payloadForCreateAppointment = null,
  aptPrice = null,
  props,
  profileDetails = null,
  refreshAptDetailScreenData = () => {},
}) => {
  const [screenState, setScreenState] = useState(-1);
  const [paymentMethod, setPaymentMethod] = useState<number | null>(-1);
  const [paymentLinkBody, setPaymentLinkBody] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [walletSubLimit, setWalletSubLimit] = useState<any>(null);
  const [appointmentCreated, setAppointmentCreated] = useState<any>(null);
  const [errTxtInCreatingApt, setErrTxtInCreatingApt] = useState("");

  const navigoDashboardUtils = new NavigoDashboardUtils();

  useEffect(() => {
    if (
      visible &&
      screenState === -1 &&
      (withCreateAppointment ||
        appointment?.paymentStatusResponse ===
          APPOINTMENT_PAYMENT_STATUS.PENDING)
    ) {
      if (withCreateAppointment && aptPrice === 0) {
        // directly create appointment without payment if price is 0
        createAppointment(
          payloadForCreateAppointment,
          SCREEN_STATE.SUCCESS_TOTAL_PAYMENT_WITH_WALLET,
        );
      } else {
        checkCurrentWalletDetails();
      }
    } else if (
      visible &&
      screenState === -1 &&
      appointment?.paymentStatusResponse ===
        APPOINTMENT_PAYMENT_STATUS.INITIATED
    ) {
      setPaymentLinkBody(appointment?.paymentDetails?.paymentLink);
      setScreenState(SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK);
    }
  }, [visible, screenState]);

  const checkCurrentWalletDetails = () => {
    let requestBody: any = null;

    // ✅ Case 1: Create appointment flow
    if (withCreateAppointment) {
      setLoading(true);
      if (!payloadForCreateAppointment) {
        showNotification({
          title: "Error",
          message: "Appointment payload is required for creating appointment",
          color: "red",
        });
        onClose();
        return;
      }

      if (aptPrice === null || aptPrice === undefined) {
        showNotification({
          title: "Error",
          message: "Appointment price is required",
          color: "red",
        });
        onClose();
        return;
      }

      requestBody = {
        patientID: payloadForCreateAppointment?.patientID,
        planID: payloadForCreateAppointment?.planID,
        serviceID: payloadForCreateAppointment?.serviceID,
        grandTotal: aptPrice,
        bookedFor:
          payloadForCreateAppointment?.bookedFor == 0
            ? payloadForCreateAppointment?.patientID
            : payloadForCreateAppointment?.bookedFor,
      };
    }

    // ✅ Case 2: Existing appointment flow
    else if (
      appointment?.id &&
      appointment?.price > 0 &&
      appointment?.paymentStatusResponse === APPOINTMENT_PAYMENT_STATUS.PENDING
    ) {
      // requestBody = {
      //   patientID: appointment?.patientID,
      //   planID: appointment?.planID,
      //   serviceID: appointment?.serviceID,
      //   grandTotal: appointment?.price,
      //   bookedFor:
      //     appointment?.bookedFor == 0
      //       ? appointment?.patientID
      //       : appointment?.bookedFor,
      // };

      setWalletSubLimit(appointment?.paymentDetails?.paymentDetails);
      setScreenState(SCREEN_STATE.SELECT_PAYMENT_METHOD);
      setLoading(false);
      return;
    } else {
      showNotification({
        title: "Error",
        message: "Invalid appointment state",
        color: "red",
      });
      onClose();
      return;
    }

    const onResponse = (res: any) => {
      setWalletSubLimit(res);
      setScreenState(SCREEN_STATE.SELECT_PAYMENT_METHOD);
      setLoading(false);
    };

    const onError = (err: any) => {
      setLoading(false);
      showNotification({
        title: "Error",
        message: `Error: ${err?.message || "Something went wrong"}`,
        color: "red",
      });
      onClose();
    };

    navigoDashboardUtils.checkCurrentWalletDetails(
      requestBody,
      onResponse,
      onError,
    );
  };

  const createAppointment = (body: any, screenNo: number) => {
    setLoading(true);
    const onResponse = (res: any) => {
      setLoading(false);
      showNotification({
        title: "Success",
        message: `Appointment created (Apt ID: ${res?.id})`,
        color: "green",
      });

      setAppointmentCreated(res);
      if (aptPrice === 0) {
        setScreenState(screenNo);
        return;
      } else {
        const paymentLink = res?.paymentDetails?.paymentLink;

        if (
          screenNo === SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK &&
          !paymentLink
        ) {
          setScreenState(
            SCREEN_STATE.APPOINTMENT_CREATED_BUT_LINK_NOT_GENERATED,
          );
          return;
        }
        setScreenState(screenNo);
        setPaymentLinkBody(paymentLink);
      }
    };
    const onError = (err: any) => {
      setLoading(false);
      setScreenState(SCREEN_STATE.ERROR_CREATING_APPOINTMENT);
      setErrTxtInCreatingApt(err?.message || "");
      showNotification({
        title: "Error",
        message: `Error: ${err?.message}`,
        color: "red",
      });
    };
    navigoDashboardUtils.createAppointment(body, onResponse, onError);
  };

  const goNext = (screenNo: number, paymentLink?: any) => {
    if (screenNo === SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS) {
      setScreenState(SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS);
      return;
    }

    const isWalletUsed = paymentMethod !== PAYMENT_METHOD.RAZORPAY_ONLY;

    if (
      (screenNo === SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK && paymentLink) ||
      screenNo === SCREEN_STATE.SUCCESS_TOTAL_PAYMENT_WITH_WALLET
    ) {
      // If creating appointment flow, call createAppointment instead
      if (withCreateAppointment) {
        if (!payloadForCreateAppointment) {
          showNotification({
            title: "Error",
            message: "Appointment payload is required for creating appointment",
            color: "red",
          });
          onClose();
          return;
        }

        // Merge the appointment body with payment link options
        const paymentLinkRequest: any = {
          paymentDetails: {
            ...walletSubLimit,
          },
          useWallet: isWalletUsed,
        };

        // Only include paymentLink if it's provided (not for WALLET_ONLY payments)
        if (paymentLink) {
          paymentLinkRequest.paymentLink = paymentLink;
        }

        const body = {
          ...payloadForCreateAppointment,
          paymentLinkRequest,
        };
        createAppointment(body, screenNo);
        return;
      } else {
        generatePaymentLink(isWalletUsed, screenNo, paymentLink);
      }
    }
  };

  const generatePaymentLink = (
    isWalletUsed: boolean,
    screenNo: number,
    paymentLink: any = null,
  ) => {
    if (!appointment?.id) {
      showNotification({
        title: "Error",
        message: "Appointment not found",
        color: "red",
      });
      onClose();
      return;
    }
    setLoading(true);

    const onResponse = (res: any) => {
      setLoading(false);
      setScreenState(screenNo);
      setPaymentLinkBody(res?.paymentLink);
      refreshAptDetailScreenData();
    };

    const onError = (err: any) => {
      setLoading(false);
      showNotification({
        title: "Error",
        message: `Error: ${err?.message || "Something went wrong"}`,
        color: "red",
      });
      onClose();
    };

    const requestBody: any = {
      appointmentId: appointment.id,
      useWallet: isWalletUsed,
    };

    // Only include paymentLink if it's provided (not for WALLET_ONLY payments)
    if (paymentLink) {
      requestBody.paymentLink = paymentLink;
    }

    navigoDashboardUtils.generatePaymentLink(requestBody, onResponse, onError);
  };

  const dialogWidth =
    screenState === SCREEN_STATE.SELECT_PAYMENT_METHOD ||
    screenState === SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS
      ? "720px"
      : "550px";

  return (
    <Dialog
      open={visible}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
      maxWidth={false}
      PaperProps={{
        style: {
          borderRadius: "10px",
          minHeight: "400px",
          width: dialogWidth,
        },
      }}
    >
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress id="payment-dialog-loading" />
        </div>
      ) : (
        <div className="payment-link-dialog-container zy-text-container">
          {/* Header */}
          {(screenState === SCREEN_STATE.SELECT_PAYMENT_METHOD ||
            screenState === SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS) && (
            <>
              <div className="pld-header">
                <div className="pld-header-title">Proceed to pay</div>
                <IconX
                  width={24}
                  height={24}
                  color="#767676"
                  style={{ cursor: "pointer" }}
                  onClick={onClose}
                ></IconX>
              </div>
              {/* Grand Total */}
              <div className="pld-total-wrapper">
                <div className="pld-total-box">
                  Grand total:{" "}
                  <span className="pld-bold">
                    {formatCurrency(walletSubLimit?.grandTotal)}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Dynamic screens */}
          {screenState === SCREEN_STATE.SELECT_PAYMENT_METHOD && (
            <SelectPaymentMethod
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              goNext={goNext}
              walletSubLimit={walletSubLimit}
              withCreateAppointment={withCreateAppointment}
            />
          )}

          {screenState === SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS && (
            <SelectShareLinkOptions
              goNext={goNext}
              profileDetails={profileDetails}
            />
          )}

          {screenState === SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK && (
            <SuccessWithGeneratedLink
              onClose={onClose}
              paymentLinkBody={paymentLinkBody}
              withCreateAppointment={withCreateAppointment}
              appointmentCreated={appointmentCreated}
              appointment={appointment}
              props={props}
              profileDetails={profileDetails}
            />
          )}

          {screenState === SCREEN_STATE.SUCCESS_TOTAL_PAYMENT_WITH_WALLET && (
            <SuccessTotalPaymentWithWallet
              onClose={onClose}
              withCreateAppointment={withCreateAppointment}
              appointmentCreated={appointmentCreated}
              props={props}
              profileDetails={profileDetails}
            />
          )}
          {screenState === SCREEN_STATE.ERROR_CREATING_APPOINTMENT && (
            <ErrorInCreatingAppointment
              onClose={() => {
                onClose();
                setScreenState(-1);
              }}
              errorText={errTxtInCreatingApt}
            />
          )}

          {screenState ===
            SCREEN_STATE.APPOINTMENT_CREATED_BUT_LINK_NOT_GENERATED && (
            <AptCreatedButLinkNotGenerated
              appointmentCreated={appointmentCreated}
              props={props}
              profileDetails={profileDetails}
            />
          )}
        </div>
      )}
    </Dialog>
  );
};

export default PaymentLinkDialog;
